import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { MobileCallBar } from "@/components/MobileCallBar";
import { Toaster } from "@/components/ui/sonner";
import { useLang } from "@/i18n/LanguageContext";
import { useJsonLd, AGENCY_SCHEMA, SITE_URL } from "@/components/Seo";

export function usePageMeta(title, description) {
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  useEffect(() => {
    const pageTitle = t(title);
    const desc = t(description || "");
    document.title = pageTitle;
    const ensure = (attr, key, content) => {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    ensure("name", "description", desc);
    const url = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
    ensure("property", "og:title", pageTitle);
    ensure("property", "og:description", desc);
    ensure("property", "og:url", url);
    ensure("property", "og:type", "website");
    ensure("property", "og:site_name", "Artha Insurance LLC");
    ensure("property", "og:locale", lang === "es" ? "es_US" : "en_US");
    ensure("property", "og:image", `${SITE_URL}/artha-logo.png`);
    ensure("name", "twitter:card", "summary");
    ensure("name", "twitter:title", pageTitle);
    ensure("name", "twitter:description", desc);
  }, [title, description, t, lang, pathname]);
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const bare = pathname.startsWith("/apply");
  const { t } = useLang();
  const lenisRef = useRef(null);
  useJsonLd("jsonld-agency", AGENCY_SCHEMA);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-white focus:px-4 focus:py-2 focus:text-[var(--ink)] focus:border focus:border-[var(--blue)]"
      >
        {t("Skip to main content")}
      </a>
      {!bare && <SiteHeader />}
      <main id="main-content" className="flex-1 pb-20 md:pb-0">{children}</main>
      {!bare && <SiteFooter />}
      {!bare && <MobileCallBar />}
      <Toaster position="top-right" richColors />
    </div>
  );
}
